// src/contexts/ToolStateContext.tsx
"use client";

import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';
import type { FirstImpressionTool } from '@/types';

interface ToolStateContextType {
  // Current state of tool confirmation
  isWaitingForConfirmation: boolean;
  pendingTools: FirstImpressionTool[] | null;
  confirmedTools: FirstImpressionTool[] | null;
  confirmationId: string | null; // Unique ID to track confirmation instances
  
  // Actions for managing tool state
  setPendingConfirmation: (tools: FirstImpressionTool[], confirmationId: string) => void;
  confirmTools: (tools: FirstImpressionTool[]) => void;
  rejectTools: () => void;
  clearConfirmation: () => void;
  isConfirmationActive: (confirmationId: string) => boolean;
  hasProcessedSignature: (signature: string) => boolean;
  markSignatureProcessed: (signature: string) => void;
  clearProcessedSignature: (signature: string) => void;
}

const ToolStateContext = createContext<ToolStateContextType | null>(null);

export const useToolState = (): ToolStateContextType => {
  const context = useContext(ToolStateContext);
  if (!context) {
    throw new Error('useToolState must be used within a ToolStateProvider');
  }
  return context;
};

interface ToolStateProviderProps {
  children: ReactNode;
}

export const ToolStateProvider: React.FC<ToolStateProviderProps> = ({ children }) => {
  const [isWaitingForConfirmation, setIsWaitingForConfirmation] = useState(false);
  const [pendingTools, setPendingTools] = useState<FirstImpressionTool[] | null>(null);
  const [confirmedTools, setConfirmedTools] = useState<FirstImpressionTool[] | null>(null);
  const [confirmationId, setConfirmationId] = useState<string | null>(null);
  const [processedSignatures, setProcessedSignatures] = useState<Set<string>>(new Set());

  const setPendingConfirmation = useCallback((tools: FirstImpressionTool[], id: string) => {
    console.log('Setting pending confirmation:', { tools, confirmationId: id });
    setIsWaitingForConfirmation(true);
    setPendingTools(tools);
    setConfirmedTools(null);
    setConfirmationId(id);
  }, []);

  const confirmTools = useCallback((tools: FirstImpressionTool[]) => {
    console.log('Tools confirmed:', tools);
    setConfirmedTools(tools);
    setIsWaitingForConfirmation(false);
  }, []);

  const rejectTools = useCallback(() => {
    console.log('Tools rejected');
    setConfirmedTools([]); // Empty array indicates rejection
    setIsWaitingForConfirmation(false);
  }, []);

  const clearConfirmation = useCallback(() => {
    console.log('Clearing tool confirmation state');
    setIsWaitingForConfirmation(false);
    setPendingTools(null);
    setConfirmedTools(null);
    setConfirmationId(null);
  }, []);

  const isConfirmationActive = useCallback((id: string) => {
    return confirmationId === id;
  }, [confirmationId]);

  const hasProcessedSignature = useCallback((signature: string) => {
    return processedSignatures.has(signature);
  }, [processedSignatures]);

  const markSignatureProcessed = useCallback((signature: string) => {
    setProcessedSignatures(prev => new Set(prev).add(signature));
  }, []);

  const clearProcessedSignature = useCallback((signature: string) => {
    setProcessedSignatures(prev => {
      const newSet = new Set(prev);
      newSet.delete(signature);
      return newSet;
    });
  }, []);

  const contextValue = useMemo((): ToolStateContextType => ({
    isWaitingForConfirmation,
    pendingTools,
    confirmedTools,
    confirmationId,
    setPendingConfirmation,
    confirmTools,
    rejectTools,
    clearConfirmation,
    isConfirmationActive,
    hasProcessedSignature,
    markSignatureProcessed,
    clearProcessedSignature
  }), [
    isWaitingForConfirmation,
    pendingTools,
    confirmedTools,
    confirmationId,
    setPendingConfirmation,
    confirmTools,
    rejectTools,
    clearConfirmation,
    isConfirmationActive,
    hasProcessedSignature,
    markSignatureProcessed,
    clearProcessedSignature
  ]);

  return (
    <ToolStateContext.Provider value={contextValue}>
      {children}
    </ToolStateContext.Provider>
  );
};